import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Customer, PaginatedResponse } from '../../types';
import { fetchCustomers, deleteCustomer, importCustomersCsv } from '../../services/api';
import { useSuperTenant } from '../../context/SuperAdminTenantContext';
import { encodeId } from '../../utils/hashids';
import CustomerForm from './CustomerForm';
import Pager from '../Pager';

const initials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const TEMP_BADGE: Record<string, { bg: string; color: string }> = {
  HOT:  { bg: '#fee2e2', color: '#991b1b' },
  WARM: { bg: '#fef9c3', color: '#854d0e' },
  COLD: { bg: '#e0f2fe', color: '#0369a1' },
};

const STATUS_BADGE: Record<string, { bg: string; color: string }> = {
  active:   { bg: '#dcfce7', color: '#166534' },
  inactive: { bg: '#f1f5f9', color: '#64748b' },
  at_risk:  { bg: '#fef9c3', color: '#854d0e' },
  churned:  { bg: '#fee2e2', color: '#991b1b' },
};

interface ImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

const CustomerList: React.FC = () => {
  const history = useHistory();
  const { tenantId } = useSuperTenant();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [data, setData]         = useState<PaginatedResponse<Customer> | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [search, setSearch]     = useState('');
  const [status, setStatus]     = useState('');
  const [source, setSource]     = useState('');
  const [temperature, setTemp]  = useState('');
  const [page, setPage]         = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState<Customer | null>(null);

  // CSV import state
  const [showImport, setShowImport]     = useState(false);
  const [importFile, setImportFile]     = useState<File | null>(null);
  const [importing, setImporting]       = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetchCustomers({
        page,
        search:      search      || undefined,
        status:      status      || undefined,
        source:      source      || undefined,
        temperature: temperature || undefined,
        tenant_id:   tenantId    || undefined,
      });
      setData(res);
    } catch { setError('Failed to fetch customers'); }
    finally  { setLoading(false); }
  }, [page, search, status, source, temperature, tenantId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status, source, temperature]);

  const handleSaved = () => { setShowForm(false); setEditing(null); load(); };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm('Delete this customer?')) return;
    try { await deleteCustomer(id); load(); }
    catch { alert('Failed to delete customer'); }
  };

  const handleEdit = (e: React.MouseEvent, c: Customer) => {
    e.stopPropagation();
    setEditing(c); setShowForm(true);
  };

  const handleRowClick = (c: Customer) => {
    history.push(`/customers/${encodeId(c.id)}`);
  };

  const clearFilters = () => { setSearch(''); setStatus(''); setSource(''); setTemp(''); };
  const hasFilters = search || status || source || temperature;

  // CSV Import
  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportFile(e.target.files?.[0] ?? null);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const res = await importCustomersCsv(importFile);
      setImportResult(res);
      if (res.imported > 0) load();
    } catch {
      setImportResult({ imported: 0, skipped: 0, errors: ['Import failed. Check file format.'] });
    } finally {
      setImporting(false);
    }
  };

  const closeImport = () => {
    setShowImport(false);
    setImportFile(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="list-page">
      {/* ── Header ── */}
      <div className="page-hd">
        <div className="page-hd-left">
          <h1 className="page-title">Customers</h1>
          <p className="page-meta"><strong>{data?.total ?? 0}</strong> total customers</p>
        </div>
        <div className="page-hd-right">
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Import CSV
          </button>
          <button className="btn btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Customer
          </button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="filter-bar">
        <div className="filter-input-wrap">
          <svg className="filter-input-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            className="filter-input"
            placeholder="Search by name, email, mobile or company…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <select className="filter-select" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="at_risk">At Risk</option>
          <option value="churned">Churned</option>
        </select>

        <select className="filter-select" value={temperature} onChange={e => setTemp(e.target.value)}>
          <option value="">All Temps</option>
          <option value="HOT">🔥 Hot</option>
          <option value="WARM">🌡 Warm</option>
          <option value="COLD">❄ Cold</option>
        </select>

        <select className="filter-select" value={source} onChange={e => setSource(e.target.value)}>
          <option value="">All Sources</option>
          <option value="manual">Manual</option>
          <option value="csv_import">CSV Import</option>
          <option value="referral">Referral</option>
          <option value="website">Website</option>
          <option value="field">Field</option>
        </select>

        {hasFilters && (
          <button className="btn btn-ghost" onClick={clearFilters}>Clear</button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* ── Table card ── */}
      <div className="data-card">
        {loading ? (
          <div className="spinner">Loading…</div>
        ) : (
          <>
            <div className="data-card-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Company</th>
                    <th>Status</th>
                    <th>Temp</th>
                    <th>Assigned</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data && data.data.length > 0 ? data.data.map(c => (
                    <tr key={c.id} onClick={() => handleRowClick(c)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div className="avatar-chip">
                          <span className="avatar-sm">{initials(c.name)}</span>
                          <div>
                            <div className="avatar-sm-name">{c.name}</div>
                            {c.city && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.city}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="td-muted">
                        <div>{c.mobile ?? c.phone ?? '—'}</div>
                        {c.email && <div style={{ fontSize: 11 }}>{c.email}</div>}
                      </td>
                      <td>{c.company ? <span className="company-tag">{c.company}</span> : <span className="td-muted">—</span>}</td>
                      <td>
                        {c.status && STATUS_BADGE[c.status] ? (
                          <span style={{
                            background: STATUS_BADGE[c.status].bg,
                            color: STATUS_BADGE[c.status].color,
                            padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                          }}>
                            {c.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        ) : <span className="td-muted">—</span>}
                      </td>
                      <td>
                        {c.temperature && TEMP_BADGE[c.temperature] ? (
                          <span style={{
                            background: TEMP_BADGE[c.temperature].bg,
                            color: TEMP_BADGE[c.temperature].color,
                            padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                          }}>
                            {c.temperature}
                          </span>
                        ) : <span className="td-muted">—</span>}
                      </td>
                      <td className="td-muted">{c.assigned_user?.name ?? '—'}</td>
                      <td>
                        <div className="row-actions" onClick={e => e.stopPropagation()}>
                          <button className="row-btn" onClick={e => handleEdit(e, c)}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            Edit
                          </button>
                          <button className="row-btn row-btn-danger" onClick={e => handleDelete(e, c.id)}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr className="table-empty-row">
                      <td colSpan={7}>
                        {hasFilters
                          ? 'No customers match your filters.'
                          : 'No customers yet. Click New Customer or Import CSV to get started.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {data && <Pager currentPage={page} lastPage={data.last_page} total={data.total} label="Customers" onChange={setPage} />}
          </>
        )}
      </div>

      {/* ── CustomerForm modal ── */}
      {showForm && (
        <CustomerForm
          customer={editing}
          onSaved={handleSaved}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      {/* ── CSV Import modal ── */}
      {showImport && (
        <div className="modal-overlay" onClick={closeImport}>
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Import Customers from CSV</h2>
              <button className="modal-close" onClick={closeImport}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#475569' }}>
                <strong>Required column:</strong> <code>name</code><br />
                <strong>Optional:</strong> email, mobile, phone, company, city, district, state, business_type, drug_licence_number, gst_number, source, notes, address
              </div>

              <label className="form-label">Select CSV file</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleImportFile}
                style={{ display: 'block', marginBottom: 12, fontSize: 13 }}
              />

              {importResult && (
                <div style={{
                  background: importResult.errors.length > 0 ? '#fef9c3' : '#dcfce7',
                  border: `1px solid ${importResult.errors.length > 0 ? '#fde68a' : '#86efac'}`,
                  borderRadius: 8, padding: '12px 16px', marginBottom: 12, fontSize: 13,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    Imported {importResult.imported} &middot; Skipped {importResult.skipped}
                  </div>
                  {importResult.errors.map((e, i) => (
                    <div key={i} style={{ color: '#92400e', fontSize: 12 }}>{e}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeImport}>
                {importResult ? 'Close' : 'Cancel'}
              </button>
              {!importResult && (
                <button
                  className="btn btn-primary"
                  disabled={!importFile || importing}
                  onClick={handleImport}
                >
                  {importing ? 'Importing…' : 'Import'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerList;
